import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './WikiEditorPopup.css';
import '../form/Form.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import { useOffline } from '../context/OfflineContext';
import useWikis, { removeWikiPage, upsertWikiPage } from '../../hooks/useWikis';
import {
  normalizeWikiIcon, normalizeWikiPage, validateWikiForm, wikiApiErrorMessage, wikiIconInput,
  WIKI_DEFAULT_ICON, WIKI_ICON_MAX, WIKI_ICON_OVERVIEW_URL, WIKI_MESSAGES, WIKI_NAME_MAX,
} from '../../util/wikis';

export const WIKI_EDITOR_POPUP = 'wikieditor';

// Position field → request value; empty/invalid → 0 (= append at the end, the server clamps as well).
function parsePosition(value) {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Popup "Neues Wiki" / "Wiki bearbeiten" (admins): name, icon (free Material Symbols name with a live preview)
 * and position among the wiki tiles on the home page. Open it with openPopup(WIKI_EDITOR_POPUP, id | 'new').
 * - wiki: the edited wiki, null = create
 * - onSaved(page): after a successful save while the editor is still open (e.g. navigate to a new wiki)
 * Saves patch the shared wiki list locally (useWikis) — no refetch.
 */
function WikiEditorPopup({ wiki = null, onSaved }) {
  const { wikis } = useWikis();
  const { isOffline } = useOffline();
  const { isPopupOpen, popupData, closePopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();

  const [form, setForm] = useState({ name: '', icon: '', position: '' });
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const editorOpen = isPopupOpen && popupData.name === WIKI_EDITOR_POPUP;
  const editorOpenRef = useRef(editorOpen);
  editorOpenRef.current = editorOpen;
  const wikiRef = useRef(wiki);
  wikiRef.current = wiki;
  const wikisRef = useRef(wikis);
  wikisRef.current = wikis;
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const saveRequestRef = useRef(0);
  const editorSessionRef = useRef(0); // +1 per opening of the editor

  // Fresh form on every open: prefilled from the edited wiki, a new one goes to the end.
  useEffect(() => {
    if (!editorOpen) return;
    editorSessionRef.current += 1;
    const current = wikiRef.current;
    setForm({
      name: current?.name ?? '',
      icon: current?.icon ?? '',
      position: String(current ? current.position : wikisRef.current.length + 1),
    });
    setSubmitted(false);
    setError('');
  }, [editorOpen]);

  const others = wikis.filter(w => w.id !== wiki?.id);
  const errors = validateWikiForm(form, others);
  // Duplicate name and icon format show while typing, a missing name after the first save attempt.
  const shownErrors = submitted
    ? errors
    : {
        name: errors.name === WIKI_MESSAGES.nameDuplicate ? errors.name : undefined,
        icon: errors.icon,
      };
  const previewIcon = errors.icon ? 'question_mark' : normalizeWikiIcon(form.icon);
  // Last useful position: behind all others (create) or the last slot (edit).
  const lastPosition = others.length + 1;

  // A well-formed but unknown name renders as text instead of a glyph and overflows the preview tile — warn
  // (not blocking: the overview may know symbols this check can't tell apart).
  const previewRef = useRef(null);
  const [unknownIcon, setUnknownIcon] = useState(false);
  useLayoutEffect(() => {
    const el = previewRef.current;
    if (!el) {
      setUnknownIcon(false);
      return;
    }
    const check = () => setUnknownIcon(el.scrollWidth > el.clientWidth + 1);
    check();
    // On the first open the symbol font may still be loading.
    document.fonts?.ready?.then(check).catch(() => {});
  }, [previewIcon, editorOpen]);

  const setField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setError('');
  };

  const handleSave = () => {
    if (saving || isOffline) return;
    setSubmitted(true);
    if (Object.keys(errors).length > 0) return;
    const target = wiki;
    const body = {
      name: form.name.trim(),
      icon: normalizeWikiIcon(form.icon),
      position: parsePosition(form.position),
    };
    // The editor may be cancelled and reopened while saving: only touch it if it still shows this request.
    const request = ++saveRequestRef.current;
    const session = editorSessionRef.current;
    const stillEditing = () => editorOpenRef.current && editorSessionRef.current === session;
    setSaving(true);
    setError('');
    startSuccessBarLoading('Wiki wird gespeichert');
    (target ? api.put(`/WikiPage/Update/${target.id}`, body) : api.post('/WikiPage/Create', body))
      .then(res => {
        upsertWikiPage(res.data);
        startSuccessBarSuccess('Wiki gespeichert');
        if (!stillEditing()) return;
        closePopup();
        onSavedRef.current?.(normalizeWikiPage(res.data));
      })
      .catch(err => {
        closeSuccessBar();
        // Deleted meanwhile (e.g. by another admin): close the editor first — dropping the wiki unmounts it on
        // the wiki page, which then says "Dieses Wiki gibt es nicht (mehr)".
        if (target && err?.response?.status === 404) {
          if (stillEditing()) closePopup();
          removeWikiPage(target.id);
          return;
        }
        if (!stillEditing()) return;
        setError(wikiApiErrorMessage(err, 'Das Wiki konnte nicht gespeichert werden.'));
      })
      .finally(() => { if (saveRequestRef.current === request) setSaving(false); });
  };

  return (
    <Popup
      name={WIKI_EDITOR_POPUP}
      headerText={wiki ? 'Wiki bearbeiten' : 'Neues Wiki'}
      confirmText={saving ? 'Speichert …' : 'Speichern'}
      type="info"
      onConfirm={handleSave}
      dontCloseOnConfirm
      withCancel
      confirmDisabled={saving || isOffline}
    >
      <div className="wikiEditorForm">
        <div className="formFieldWrapper">
          <label className="formFieldLabel" htmlFor="wikiEditorName">Name</label>
          <input
            id="wikiEditorName"
            type="text"
            maxLength={WIKI_NAME_MAX}
            placeholder="z. B. Regelwerk"
            autoComplete="off"
            value={form.name}
            onChange={e => setField('name', e.target.value)}
            aria-invalid={shownErrors.name ? 'true' : undefined}
            className={shownErrors.name ? 'isInvalid' : ''}
          />
        </div>
        {shownErrors.name && <p className="wikiEditorFieldError">{shownErrors.name}</p>}

        <div className="wikiEditorIconRow">
          <div className="formFieldWrapper">
            <label className="formFieldLabel" htmlFor="wikiEditorIcon">Icon</label>
            <input
              id="wikiEditorIcon"
              type="text"
              maxLength={WIKI_ICON_MAX}
              placeholder={`z. B. ${WIKI_DEFAULT_ICON}`}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              value={form.icon}
              onChange={e => setField('icon', wikiIconInput(e.target.value))}
              aria-invalid={shownErrors.icon ? 'true' : undefined}
              aria-describedby="wikiEditorIconHint"
              className={shownErrors.icon ? 'isInvalid' : ''}
            />
          </div>
          <span
            ref={previewRef}
            className={`material-symbols-rounded wikiEditorIconPreview${errors.icon || unknownIcon ? ' isInvalid' : ''}`}
            title="Vorschau"
            aria-hidden="true"
          >
            {previewIcon}
          </span>
        </div>
        {shownErrors.icon && <p className="wikiEditorFieldError">{shownErrors.icon}</p>}
        {!errors.icon && unknownIcon && (
          <p className="wikiEditorFieldWarning" role="status">
            Dieses Icon gibt es vermutlich nicht – bitte den Namen in der Icon-Übersicht prüfen.
          </p>
        )}
        <p className="formFieldHint" id="wikiEditorIconHint">
          Name eines Material Symbols, leer = „{WIKI_DEFAULT_ICON}“. Alle Namen findest du in der{' '}
          <a className="wikiEditorLink" href={WIKI_ICON_OVERVIEW_URL} target="_blank" rel="noopener noreferrer">
            Icon-Übersicht
          </a>.
        </p>

        <div className="formFieldWrapper">
          <label className="formFieldLabel" htmlFor="wikiEditorPosition">Position</label>
          <input
            id="wikiEditorPosition"
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            placeholder="leer = ans Ende"
            value={form.position}
            onChange={e => setField('position', e.target.value)}
            aria-describedby="wikiEditorPositionHint"
          />
        </div>
        <p className="formFieldHint" id="wikiEditorPositionHint">
          Reihenfolge der Kacheln unter „Wikis“ auf der Startseite: 1 = ganz vorne, {lastPosition} = am Ende.
        </p>

        {isOffline && (
          <div className="formOfflineHint">
            <span className="material-symbols-rounded formOfflineHintIcon" aria-hidden="true">cloud_off</span>
            Offline – Speichern nicht verfügbar
          </div>
        )}
        {error && <div className="newError" role="alert">{error}</div>}
      </div>
    </Popup>
  );
}

export default WikiEditorPopup;
